import React from "react";
import {
  Badge,
  Flex,
  Heading,
  Button,
  Menu,
  MenuItem,
  MenuButton,
  View,
} from "@aws-amplify/ui-react";
import { NavLink, Link } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { Settings as SettingsIcon, Menu as MenuIcon } from "lucide-react";
import UserMenu from "../components/UserMenu";
import { useAdminTenant } from "../admin/AdminTenantContext";
import TenantSwitcherCompact from "../components/admin/TenantSwitcherCompact";
import LogoMark from "../components/LogoMark";


const navItems = [
  { label: "Devices", to: "/devices" },
  { label: "Events", to: "/events" },
  { label: "Settings", to: "/settings", icon: <SettingsIcon size={16} /> },
];

export default function TopNav() {
  const { authStatus, user } = useAuthenticator((ctx) => [ctx.authStatus, ctx.user]);

  const { isPlatformAdmin, adminTenantKey, setAdminTenantKey } = useAdminTenant();

  if (authStatus === "configuring") return null;

  const isAuthed = authStatus === "authenticated" && !!user;

  return (
    <Flex
      as="header"
      backgroundColor="var(--amplify-colors-neutral-20)"
      boxShadow="0 1px 0 rgba(0,0,0,0.04)"
      borderBottom="none"
      top="0"
      zIndex="10"
    >
      <Flex
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        maxWidth="1200px"
        margin="0 auto"
        padding="0.75rem 1.5rem"
        gap="0.75rem"
        direction={{ base: "column", medium: "row" }}
      >
        {/* Brand / Title */}
        <Link to="/" style={{ textDecoration: "none" }}>
          <Flex
            alignItems="center"
            gap="0.5rem"
            style={{ textDecoration: "none" }}
            justifyContent={{ base: "center", medium: "flex-start" }}
          >
            <View
              aria-hidden="true"
              style={{
                color: "var(--amplify-colors-teal-60)", // tweak to taste
                lineHeight: 0,
                display: "flex",
                alignItems: "center",
              }}
            >
              <LogoMark size={32} />
            </View>

            <Heading
              level={4}
              margin="0"
              lineHeight="1"
              fontWeight="700"
              textAlign={{ base: "center", medium: "left" }}
              color="var(--amplify-colors-neutral-100)"
            >
              Plex LD
            </Heading>
          </Flex>
        </Link>

        {/* Right side */}
        <Flex
          as="nav"
          alignItems="center"
          justifyContent={{ base: "space-between", medium: "flex-end" }}
          width={{ base: "100%", medium: "auto" }}
          gap="0.75rem"
        >
          {!isAuthed ? (
            <Button as={Link} to="/login" size="small" variation="primary">
              Sign in
            </Button>
          ) : (
            <>
              {isPlatformAdmin && (
                <Flex
                  direction="column"
                  alignItems="stretch"
                  gap="0.25rem"
                  display={{ base: "flex", medium: "none" }}
                  width="100%"
                >
                  <TenantSwitcherCompact value={adminTenantKey} onChange={setAdminTenantKey} />
                  {adminTenantKey ? (
                    <Badge variation="warning">Admin: Viewing as</Badge>
                  ) : (
                    <Badge variation="info">Admin: No tenant</Badge>
                  )}
                </Flex>
              )}
              {/* Mobile: Menu dropdown */}
              <View display={{ base: "block", medium: "none" }}>
                <Menu 
                    trigger={
                        <MenuButton 
                        variation="link" 
                        size="small" 
                        border="1px solid var(--amplify-colors-neutral-40)" // Adds the border
                        borderRadius="4px"
                        ariaLabel="Open navigation menu">
                            <MenuIcon size={16}/>
                        </MenuButton>
                    }
                >
                    {navItems.map((item) => (
                        <MenuItem
                        key={item.to}
                        as={NavLink}
                        to={item.to}
                        end
                        >
                        <Flex alignItems="center" gap="0.5rem">
                            {item.icon ?? null}
                            <span>{item.label}</span>
                        </Flex>
                        </MenuItem>
                    ))}
                </Menu>
              </View>

              {/* Desktop: inline nav */}
              <Flex
                display={{ base: "none", medium: "flex" }}
                gap="1rem"
                alignItems="center"
              >
                {navItems.map((item) => {
                  const isSettings = item.to === "/settings";
                  return (
                    <Button
                      key={item.to}
                      as={NavLink}
                      to={item.to}
                      end
                      size="small"
                      variation="link"
                      className="topnav-link"
                      ariaLabel={item.label}
                      title={item.label}
                    >
                      {isSettings ? (
                        <Flex alignItems="center" gap="0.4rem">
                          {item.icon}
                          <span>{item.label}</span>
                        </Flex>
                      ) : (
                        item.label
                      )}
                    </Button>
                  );
                })}
              </Flex>

              {isPlatformAdmin && (
                <Flex alignItems="flex-end" gap="0.5rem" display={{ base: "none", medium: "flex" }}>
                  <View>
                    <TenantSwitcherCompact value={adminTenantKey} onChange={setAdminTenantKey} />
                  </View>
                  {adminTenantKey ? (
                    <Badge variation="warning">Admin: Viewing as</Badge>
                  ) : (
                    <Badge variation="info">Admin: No tenant</Badge>
                  )}
                </Flex>
              )}
              {/* User menu always visible */}
              <UserMenu />
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
}
